import React, { useState, useContext } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Collapse,
  IconButton,
  Typography,
  Snackbar
} from '@mui/material';
import {
  Email as EmailIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';
import authService from '../services/authService';
import demoService from '../services/demoService';

const EmailVerificationBanner = () => {
  const { currentUser, fetchUserData } = useContext(AuthContext);
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  // Don't show if user is verified, not logged in, banner is dismissed, or in demo mode
  if (!currentUser || currentUser.email_verified || dismissed || demoService.isDemoMode()) {
    return null;
  }

  const handleResendEmail = async () => {
    setResending(true);
    setResendError('');
    
    try {
      const response = await authService.resendVerificationEmail(currentUser.email);
      
      if (response.success) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setResendError(response.error || 'Failed to resend verification email');
      }
    } catch (error) {
      console.error('Error resending verification email:', error);
      setResendError('Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      await fetchUserData();
    } catch (error) {
      console.error('Error refreshing user status:', error);
    }
  };

  return (
    <>
      <Collapse in={!dismissed}>
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleRefreshStatus}
                title="Refresh verification status"
                sx={{ color: 'warning.main' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setDismissed(true)}
                title="Dismiss notification"
                sx={{ color: 'warning.main' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          icon={<EmailIcon />}
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
            Please verify your email address
          </AlertTitle>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            We've sent a verification email to <strong>{currentUser.email}</strong>. 
            Please check your inbox and click the verification link to activate your account.
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResendEmail}
              disabled={resending}
              startIcon={resending ? <RefreshIcon /> : <EmailIcon />}
              sx={{
                borderColor: 'warning.main',
                color: 'warning.main',
                '&:hover': {
                  borderColor: 'warning.dark',
                  backgroundColor: 'warning.light',
                  color: 'warning.dark'
                }
              }}
            >
              {resending ? 'Sending...' : 'Resend Email'}
            </Button>
            
            <Button
              size="small"
              variant="text"
              onClick={handleRefreshStatus}
              startIcon={<CheckIcon />}
              sx={{
                color: 'warning.main',
                '&:hover': {
                  backgroundColor: 'warning.light',
                  color: 'warning.dark'
                }
              }}
            >
              I've verified
            </Button>
          </Box>

          {resendError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              {resendError}
            </Typography>
          )}
        </Alert>
      </Collapse>

      {/* Success snackbar for resend confirmation */}
      <Snackbar
        open={resendSuccess}
        autoHideDuration={5000}
        onClose={() => setResendSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setResendSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Verification email sent! Please check your inbox.
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailVerificationBanner;
